import { Button, Icon, InputBase, Loading, OverviewHeader } from '@brighterion/br-ui';
import { Field, FieldProps, useFormikContext } from 'formik';
import React from 'react';
import { DateService } from 'Services';
import clsx from 'clsx';
import { ADVANCED_RULE_PARAMETER_MAPPING } from 'Common/enums';
import { CREATE_EXPRESSION_PARAM_INPUTS, ParameterTppType } from 'Common/parameterEnums';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import useValidateRuleExpression from 'Hooks/query-hooks/global-rules/useValidateRuleExpression';
import useGetIntellisenseData from 'Hooks/query-hooks/global-rules/useGetIntellisenseData';
import useValidateParameterExpression from 'Hooks/query-hooks/parameters/useValidateParameterExpression';
import { generateParameterSystemName } from 'Utils';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import { getUser } from 'Selectors';

import styles from './ScriptOverview.css';
import BrightScriptEditor from '../BrightScriptEditor';

type ScriptOverviewProps = {
    inputFields: {
        className?: string;
        disabled?: boolean;
        id?: string;
        label: string;
        name: string;
    }[];
    scriptType: 'rule' | 'parameter';
    leftTitle: string;
    rightTitle?: string;
    parameterData?: UseGetParametersResponse | undefined;
    listData?: ListManagementApiList[];
    readOnly: boolean;
};

const ScriptOverview = ({
    scriptType,
    inputFields,
    leftTitle,
    rightTitle,
    parameterData,
    listData,
    readOnly = false
}: ScriptOverviewProps) => {
    const { t } = useTranslation();
    const { setFieldValue, handleBlur, handleChange, initialValues, values } = useFormikContext<
        ParameterExpressionFormSchema | AdvancedRuleFormSchema
    >();
    const { isLoading, data: intellisenseData, error: intellisenseError } = useGetIntellisenseData();
    const [editorValue, setEditorValue] = React.useState<string | undefined>(initialValues.expression.expText);
    const [validationMsg, setValidationMsg] = React.useState<string[] | null>(null);
    const { landlordId, tenantKey } = useSelector(getUser);
    const isImpersonationMode = landlordId && landlordId !== tenantKey;

    const supportedParameterTppTypes: string[] = Object.values(ParameterTppType);

    React.useEffect(() => {
        if (scriptType === 'parameter') {
            if ((values as ParameterExpressionFormSchema).revalidateScript && !values.isScriptValidated) {
                setValidationMsg(null);
                setFieldValue('revalidateScript', true);
            }
        }
    }, [setValidationMsg, setFieldValue, scriptType, values]);

    const updateScriptValidity = (isValid: boolean) => {
        setFieldValue('isScriptValidated', isValid);
    };

    const onError = (error: any) => {
        updateScriptValidity(false);
        if (error?.response?.status === 400) {
            setValidationMsg(error?.response?.data?.messages || error?.response?.data);
        } else {
            /** Fallback: If other endpoints returning the old error object */
            setValidationMsg(error?.data?.messages || error?.data);
        }
    };

    const onSuccess = (response: any) => {
        const { data, status } = response;

        if (status === 200) {
            if (scriptType === 'parameter') {
                // System name is dynamically generated to support ux requirement
                // however, it doesn't automatically update formik values
                // so, manually set the field value
                const formattedSystemName = generateParameterSystemName(
                    (values as ParameterExpressionFormSchema).displayName
                );
                // lowercase: double, int, string, etc
                const newParameterTppType: string = data?.inputs?.[formattedSystemName]?.[1] ?? '';

                // Validate response data for supported parameter types
                if (!supportedParameterTppTypes.includes(newParameterTppType)) {
                    updateScriptValidity(false);
                    setValidationMsg([`Expressions of type ${newParameterTppType} are not supported`]);
                    return;
                }

                setFieldValue(CREATE_EXPRESSION_PARAM_INPUTS.TPP_TYPE, newParameterTppType, false);
            }

            setFieldValue('expression', data);
            updateScriptValidity(true);
            setValidationMsg([t('validationSuccessMsg')]);
        } else {
            updateScriptValidity(false);
        }
    };

    const ruleMutation = useValidateRuleExpression({
        mutationOptions: {
            onSuccess,
            onError
        }
    });

    const parameterMutation = useValidateParameterExpression({
        mutationOptions: {
            onSuccess,
            onError
        }
    });

    const handleEditorChange = (value: string | undefined) => {
        /** @note Editor value is stored and validated by the Expression endpoint */
        setEditorValue(value);
        updateScriptValidity(false);

        if (validationMsg) {
            setValidationMsg(null);
        }
    };

    /**
     * @note Intellisense Data needs to be loaded before the Monaco editor can be rendered.
     * It will cause a memory leak if the loading component is wrapped around the BrightScriptEditor.
     */
    if (isLoading || parameterData === undefined) {
        return (
            <div className={styles.spinner}>
                <Loading />
            </div>
        );
    }

    const handleTrackEvent = () => {
        let clickIdentifier = 'Advanced Rule - Validate Script';
        let text = 'Validate Script';
        if (isImpersonationMode) {
            clickIdentifier = 'Advanced Tenant Rule - Validate Script';
            text = 'Validate Advanced Tenant Rule Script';
        }
        if (scriptType === 'parameter') {
            clickIdentifier = 'Parameters - Validate Expression';
            text = 'Validate Expression';
        }
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier,
                text
            }
        });
    };

    const handleValidateScript = () => {
        const modelFields: { [fieldNameKey: string]: string } = {};

        parameterData.original.forEach((parameter) => {
            const { type, tppType, fieldName } = parameter;
            // if user is editing an existing parameter, modelFields should not include the parameter. Otherwise,
            // the script validator treats it as an existing parameter in the system and prevents user from updating it,
            // thus, return all parameters excluding the current parameter
            if (fieldName !== (values as ParameterExpressionFormSchema).systemName) {
                const fieldNameKey = fieldName.toLowerCase();
                modelFields[fieldNameKey] = tppType || ADVANCED_RULE_PARAMETER_MAPPING[type];
            }
        });

        if (scriptType === 'parameter') {
            const formattedSystemName = generateParameterSystemName(
                (values as ParameterExpressionFormSchema).displayName
            );

            parameterMutation.mutate({
                modelFields,
                outFields: [formattedSystemName],
                expText: editorValue
            });
        } else {
            const listNames = listData?.map((list) => list.name) ?? [];

            ruleMutation.mutate({
                modelFields,
                listNames,
                expText: editorValue
            });
        }

        handleTrackEvent();
    };

    return (
        <OverviewHeader
            containerClassName={styles.overviewHeader}
            dataTestId='RuleScriptOverview'
            leftTitle={leftTitle}
            rightTitle={rightTitle}
        >
            <div className={styles.overviewWrapper}>
                <div>{!readOnly ? t('scriptMsg', { subject: scriptType }) : t('readOnlyScript')}</div>
                {intellisenseError && <div className={styles.errorMsg}>{t('intellisenseErrorMsg')}</div>}
                <BrightScriptEditor
                    editorValue={editorValue}
                    handleEditorChange={handleEditorChange}
                    intellisenseData={intellisenseData}
                    parameterData={parameterData}
                    listData={listData}
                    readOnly={readOnly}
                />
                {/**
                 * Hide validation in Read Only mode.
                 * @note Please make sure Field `isScriptValidated: true` during initialization the Form values.
                 */}
                {!readOnly && (
                    <>
                        <div
                            className={clsx(styles.validationMsgWrapper, {
                                [styles.error]: !values.isScriptValidated && validationMsg,
                                [styles.success]: values.isScriptValidated && validationMsg
                            })}
                        >
                            {Array.isArray(validationMsg) &&
                                validationMsg.map((msg) => {
                                    return (
                                        <div key={msg} className={styles.validationMsgContainer}>
                                            <div className={styles.currentDate}>
                                                {DateService.getDateTime(new Date().toString())}
                                            </div>
                                            <div className={styles.validationMsg}>{msg}</div>
                                            <Icon name={values.isScriptValidated ? 'small-success' : 'small-error'} />
                                        </div>
                                    );
                                })}
                        </div>

                        <Button
                            clickCallback={handleValidateScript}
                            variant='contextual-secondary'
                            className={styles.validateBtn}
                            disabled={!!validationMsg}
                        >
                            {t('Validate Script')}
                        </Button>
                    </>
                )}
                {inputFields.map((input) => {
                    const { name, className, disabled, id, label } = input;

                    return (
                        <Field name={name} key={name}>
                            {({ field, form: { errors, touched } }: FieldProps) => (
                                <InputBase
                                    className={className}
                                    error={!!(errors[field.name] && touched[field.name])}
                                    errorMsg={errors[field.name]?.toString()}
                                    disabled={disabled}
                                    id={id ?? name}
                                    label={label}
                                    name={name}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    size='sm'
                                    value={field.value ?? ''}
                                />
                            )}
                        </Field>
                    );
                })}
            </div>
        </OverviewHeader>
    );
};

export default ScriptOverview;
